import { useEffect, useState } from "react";
import { useUser } from "@/src/context/user-context";
import { useDebounce } from "@/src/hooks/use-debounce";
import { logger } from "@/src/lib/logging/logger";
import { NPI_NUMBER_REGEX, mergeNpiResultsByNumber, searchProvidersByName, searchProvidersByNPI, searchProvidersByFirstOrLastName, type NPIResult } from "@/src/lib/npi";
import { getOperatingStatesForGroup } from "@/src/models/group";

// Maximum number of results to return per query.
// For queries where we try "flipping" and searching by both first and last name, we return this limit PER request.
const PER_STATE_LIMIT = 5;
interface NPISearchResult {
  results: NPIResult[];
  loading: boolean;
  error: string | null;
}
export function useNPISearch(searchTerm: string) {
  const {
    group
  } = useUser();
  const allowedStates = group ? getOperatingStatesForGroup(group) : undefined;
  const [state, setState] = useState<NPISearchResult>({
    results: [],
    loading: false,
    error: null
  });
  const debouncedSearchTerm = useDebounce(searchTerm, 200);
  useEffect(() => {
    const searchProviders = async () => {
      if (!debouncedSearchTerm || debouncedSearchTerm.length < 3) {
        setState({
          results: [],
          loading: false,
          error: null
        });
        return;
      }
      setState(prev => ({
        ...prev,
        loading: true,
        error: null
      }));
      try {
        const tokens = debouncedSearchTerm.trim().split(/\s+/);
        let mergedResults: NPIResult[] = [];

        // If 1 token, search by NPI number or ambiguous first/last name token
        if (tokens.length === 1) {
          const token = tokens[0];
          if (NPI_NUMBER_REGEX.test(token)) {
            const response = await searchProvidersByNPI(token, 1);
            mergedResults = response.results ?? [];
          } else {
            const response_0 = await searchProvidersByFirstOrLastName(token, allowedStates, PER_STATE_LIMIT);
            mergedResults = response_0.results ?? [];
          }
        }

        // If 2 tokens, search by name in both directions (first name, last name and last name, first name)
        else if (tokens.length === 2) {
          const [r1, r2] = await Promise.all([searchProvidersByName(tokens[0], tokens[1], allowedStates, PER_STATE_LIMIT), searchProvidersByName(tokens[1], tokens[0], allowedStates, PER_STATE_LIMIT)]);
          mergedResults = mergeNpiResultsByNumber([r1.results ?? [], r2.results ?? []]);
        }

        // If 3 tokens, assume 3rd token is state and search by name in that state
        else if (tokens.length === 3) {
          const state_0 = tokens[2];
          const [r1_0, r2_0] = await Promise.all([searchProvidersByName(tokens[0], tokens[1], [state_0], PER_STATE_LIMIT), searchProvidersByName(tokens[1], tokens[0], [state_0], PER_STATE_LIMIT)]);
          mergedResults = mergeNpiResultsByNumber([r1_0.results ?? [], r2_0.results ?? []]);
        }
        setState({
          results: mergedResults,
          loading: false,
          error: null
        });
      } catch (error) {
        logger.error("NPI search error:", {
          error
        });
        setState({
          results: [],
          loading: false,
          error: "Failed to search NPI registry"
        });
      }
    };
    searchProviders();
  }, [debouncedSearchTerm, allowedStates]);
  return state;
}